class BrandGridSlider extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.pointerId = null;
    this.dragDistance = 0;
    this.resizeObserver = null;

    this.onTransitionEnd = this.onTransitionEnd.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onDragStart = this.onDragStart.bind(this);
  }

  connectedCallback() {
    this.track = this.querySelector('.brand-grid__logos');
    if (!this.track || this.track.children.length < 2) return;

    this.originalItems = Array.from(this.track.children);
    this.track.addEventListener('transitionend', this.onTransitionEnd);
    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('pointermove', this.onPointerMove);
    this.addEventListener('pointerup', this.onPointerUp);
    this.addEventListener('pointercancel', this.onPointerUp);
    this.addEventListener('keydown', this.onKeyDown);
    this.addEventListener('click', this.onClick, true);
    this.addEventListener('dragstart', this.onDragStart);

    this.resizeObserver = new ResizeObserver(() => this.build());
    this.resizeObserver.observe(this);
    this.build();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.track?.removeEventListener('transitionend', this.onTransitionEnd);
    this.removeEventListener('pointerdown', this.onPointerDown);
    this.removeEventListener('pointermove', this.onPointerMove);
    this.removeEventListener('pointerup', this.onPointerUp);
    this.removeEventListener('pointercancel', this.onPointerUp);
    this.removeEventListener('keydown', this.onKeyDown);
    this.removeEventListener('click', this.onClick, true);
    this.removeEventListener('dragstart', this.onDragStart);
  }

  get visibleCount() {
    if (window.matchMedia('(max-width: 749px)').matches) return 2;
    if (window.matchMedia('(max-width: 1199px)').matches) return 4;
    return 6;
  }

  build() {
    this.track.querySelectorAll('[data-brand-grid-clone]').forEach((item) => item.remove());

    this.cloneCount = Math.min(this.visibleCount, this.originalItems.length);
    const gap = Number.parseFloat(getComputedStyle(this.track).gap) || 0;
    const itemSize = this.visibleCount === 2
      ? 120
      : Math.min(222, (this.clientWidth - gap * (this.visibleCount - 1)) / this.visibleCount);
    this.style.setProperty('--brand-grid-item-size', `${itemSize}px`);

    const firstItems = this.originalItems.slice(0, this.cloneCount);
    const lastItems = this.originalItems.slice(-this.cloneCount);
    const leadingClones = document.createDocumentFragment();
    lastItems.forEach((item) => leadingClones.append(this.createClone(item)));
    this.track.prepend(leadingClones);
    firstItems.forEach((item) => this.track.append(this.createClone(item)));

    // Keep the first visible brand consistent across all breakpoints.
    this.currentIndex = this.cloneCount;
    this.move(false);
  }

  createClone(item) {
    const clone = item.cloneNode(true);
    clone.dataset.brandGridClone = '';
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
    return clone;
  }

  itemStep() {
    const item = this.track.querySelector('.brand-grid__item');
    const gap = Number.parseFloat(getComputedStyle(this.track).gap) || 0;
    return item.getBoundingClientRect().width + gap;
  }

  translateForIndex(index = this.currentIndex) {
    return -index * this.itemStep();
  }

  move(animate = true) {
    this.track.style.transition = animate ? 'transform 450ms cubic-bezier(0.22, 1, 0.36, 1)' : 'none';
    this.track.style.transform = `translate3d(${this.translateForIndex()}px, 0, 0)`;
  }

  next() {
    this.currentIndex += 1;
    this.move(true);
  }

  previous() {
    this.currentIndex -= 1;
    this.move(true);
  }

  onPointerDown(event) {
    if (event.button !== 0 && event.pointerType === 'mouse') return;
    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.dragDistance = 0;
    this.track.style.transition = 'none';
  }

  onPointerMove(event) {
    if (event.pointerId !== this.pointerId) return;
    this.dragDistance = event.clientX - this.startX;

    if (Math.abs(this.dragDistance) > 5 && !this.hasPointerCapture(event.pointerId)) {
      this.classList.add('is-dragging');
      this.setPointerCapture(event.pointerId);
    }

    this.track.style.transform = `translate3d(${this.translateForIndex() + this.dragDistance}px, 0, 0)`;
  }

  onPointerUp(event) {
    if (event.pointerId !== this.pointerId) return;
    if (this.hasPointerCapture(event.pointerId)) {
      this.releasePointerCapture(event.pointerId);
    }
    this.pointerId = null;
    this.classList.remove('is-dragging');

    const threshold = Math.min(50, this.itemStep() * 0.2);
    if (this.dragDistance <= -threshold) {
      this.next();
    } else if (this.dragDistance >= threshold) {
      this.previous();
    } else {
      this.move(true);
    }
  }

  onTransitionEnd(event) {
    if (event.propertyName !== 'transform') return;

    if (this.currentIndex >= this.originalItems.length + this.cloneCount) {
      this.currentIndex = this.cloneCount;
      this.move(false);
    } else if (this.currentIndex < this.cloneCount) {
      this.currentIndex = this.originalItems.length + this.cloneCount - 1;
      this.move(false);
    }
  }

  onKeyDown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
    }
  }

  onClick(event) {
    if (Math.abs(this.dragDistance) > 5) {
      event.preventDefault();
      event.stopPropagation();
      this.dragDistance = 0;
    }
  }

  onDragStart(event) {
    event.preventDefault();
  }
}

if (!customElements.get('brand-grid-slider')) {
  customElements.define('brand-grid-slider', BrandGridSlider);
}
